import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Form, Row, Col, Card, Button, Spinner, Alert } from 'react-bootstrap';
import PageHeader from '../components/PageHeader';
import { adminApi, extractError } from '../api/client';
import { US_STATES } from '../lib/lookups';
import { useToast } from '../contexts/ToastContext';
import CivicRulesPanel from '../components/CivicRulesPanel';
import CountyMapPanel from '../components/CountyMapPanel';

const EMPTY = {
  name: '',
  abbreviation: '',
  slug: '',
  state_fips: '',
  official_election_website: '',
  notes: '',
  status: 'active',
};

export default function StateFormPage({ mode }) {
  const navigate = useNavigate();
  const { stateId } = useParams();
  const toast = useToast();
  const [data, setData] = useState(EMPTY);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(mode === 'edit');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (mode !== 'edit') return;
    let active = true;
    (async () => {
      try {
        const res = await adminApi.get(`/admin/states/${stateId}`);
        if (!active) return;
        setData(res.data);
        setOriginal(res.data);
      } catch (e) {
        if (active) setError(extractError(e));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [stateId, mode]);

  function update(field, value) {
    setData((d) => ({ ...d, [field]: value }));
  }

  function validate() {
    const errs = {};
    if (!data.name?.trim()) errs.name = 'Required';
    if (!data.abbreviation || data.abbreviation.length !== 2 || !/^[A-Za-z]{2}$/.test(data.abbreviation)) {
      errs.abbreviation = 'Must be a 2-letter code';
    }
    if (data.state_fips && !/^\d{1,2}$/.test(data.state_fips)) {
      errs.state_fips = 'Must be a 1-2 digit numeric FIPS code (e.g., 12 for FL)';
    }
    if (data.official_election_website && !/^https?:\/\//i.test(data.official_election_website)) {
      errs.official_election_website = 'Must start with http(s)://';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!validate()) {
      setError('Please correct the highlighted fields.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...data, abbreviation: (data.abbreviation || '').toUpperCase() };
      if (mode === 'create') {
        const res = await adminApi.post('/admin/states', payload);
        toast.success('State created');
        navigate(`/admin/states/${res.data.id}`);
      } else {
        const res = await adminApi.put(`/admin/states/${stateId}`, payload);
        setData(res.data);
        setOriginal(res.data);
        toast.success('State updated');
      }
    } catch (e) {
      const msg = extractError(e);
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <>
        <PageHeader title={mode === 'create' ? 'New state' : 'Edit state'} />
        <div className="text-center p-5"><Spinner animation="border" /></div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={mode === 'create' ? 'Add state' : `${original?.name || 'Edit state'}`}
        breadcrumb={
          <span>
            <Link to="/admin/states" className="text-decoration-none">States</Link> / {mode === 'create' ? 'New' : (original?.name || 'Edit')}
          </span>
        }
        actions={
          <div className="d-flex gap-2">
            {mode === 'edit' && (
              <>
                <Link to={`/admin/states/${stateId}/data-management`}>
                  <Button variant="outline-secondary" data-testid="state-view-data-management-button">
                    <i className="bi bi-database me-1" /> Data Management
                  </Button>
                </Link>
                <Button
                  variant="outline-secondary"
                  onClick={() => {
                    const el = document.querySelector('[data-testid="county-map-panel"]');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  }}
                  data-testid="state-view-county-map-button"
                >
                  <i className="bi bi-map me-1" /> County Map
                </Button>
                <Link to={`/admin/states/${stateId}/counties`}>
                  <Button variant="outline-secondary" data-testid="state-view-counties-button">
                    <i className="bi bi-list-ul me-1" /> Counties ({original?.counties_count ?? 0})
                  </Button>
                </Link>
              </>
            )}
            <Button variant="outline-secondary" onClick={() => navigate('/admin/states')}>Cancel</Button>
            <Button variant="primary" onClick={handleSubmit} disabled={saving} data-testid="state-form-save-button">
              {saving ? <Spinner size="sm" animation="border" /> : (mode === 'create' ? 'Create state' : 'Save changes')}
            </Button>
          </div>
        }
      />
      <div className="page-content">
        {error && <Alert variant="danger">{error}</Alert>}
        <Form noValidate onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col xs={12} lg={8}>
              <Card>
                <Card.Body>
                  <Row className="g-3">
                    <Col xs={12} md={8}>
                      <Form.Group>
                        <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.name} value={data.name || ''} onChange={(e) => update('name', e.target.value)} placeholder="e.g., Florida" data-testid="state-name-input" />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <Form.Label>Abbreviation <span className="text-danger">*</span></Form.Label>
                        <Form.Control isInvalid={!!errors.abbreviation} value={data.abbreviation || ''} maxLength={2} onChange={(e) => update('abbreviation', e.target.value.toUpperCase())} placeholder="FL" data-testid="state-abbreviation-input" />
                        <Form.Control.Feedback type="invalid">{errors.abbreviation}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={6} md={4}>
                      <Form.Group>
                        <Form.Label>State FIPS</Form.Label>
                        <Form.Control
                          isInvalid={!!errors.state_fips}
                          value={data.state_fips || ''}
                          maxLength={2}
                          onChange={(e) => update('state_fips', e.target.value.replace(/\D/g, '').slice(0, 2))}
                          placeholder="12"
                          data-testid="state-fips-input"
                        />
                        <Form.Control.Feedback type="invalid">{errors.state_fips}</Form.Control.Feedback>
                        <Form.Text>2-digit US Census code. Used when generating the county map (auto-derived from abbreviation if blank).</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Slug</Form.Label>
                        <Form.Control value={data.slug || ''} onChange={(e) => update('slug', e.target.value)} placeholder="auto-generated from name" data-testid="state-slug-input" />
                        <Form.Text>URL-friendly identifier. Auto-generated if blank.</Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12} md={6}>
                      <Form.Group>
                        <Form.Label>Status</Form.Label>
                        <Form.Select value={data.status || 'active'} onChange={(e) => update('status', e.target.value)} data-testid="state-status-select">
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Official election website</Form.Label>
                        <Form.Control isInvalid={!!errors.official_election_website} value={data.official_election_website || ''} onChange={(e) => update('official_election_website', e.target.value)} placeholder="https://…" data-testid="state-website-input" />
                        <Form.Control.Feedback type="invalid">{errors.official_election_website}</Form.Control.Feedback>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label>Notes</Form.Label>
                        <Form.Control as="textarea" rows={4} value={data.notes || ''} onChange={(e) => update('notes', e.target.value)} placeholder="Internal notes (election deadlines, registration rules, etc.)" data-testid="state-notes-input" />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} lg={4}>
              <Card>
                <Card.Header>Tips</Card.Header>
                <Card.Body style={{ fontSize: '0.88rem' }}>
                  <ul className="mb-2 ps-3">
                    <li>Use the official USPS 2-letter code (e.g., FL, CA, NY).</li>
                    <li>Slugs must be unique across all states.</li>
                    <li>Set <em>Inactive</em> to hide a state from voter-facing views without deleting data.</li>
                  </ul>
                  <div className="text-muted" style={{ fontSize: '0.82rem' }}>
                    Common USPS abbreviations: {US_STATES.map(([c]) => c).slice(0, 10).join(', ')}…
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
        {mode === 'edit' && <CivicRulesPanel stateId={stateId} stateName={original?.name} />}
        {mode === 'edit' && (
          <CountyMapPanel
            stateId={stateId}
            stateAbbreviation={original?.abbreviation}
            stateFips={original?.state_fips}
          />
        )}
      </div>
    </>
  );
}
